import React, { forwardRef, type PointerEvent, type ReactNode } from "react";
import { GripVertical } from "lucide-react";
import { cn } from "../../lib/utils.js";

export interface StickyNoteCardProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "onPointerDown"
> {
  /** Header label (rendered small-caps). Default `"NOTE"`. */
  label?: string;
  /** Header-left slot — the grip. Supply an interactive drag handle here (e.g. a
   *  button that spreads the sortable's `handleProps`, which carries its own
   *  focus treatment); defaults to a decorative grip. */
  leadingSlot?: ReactNode;
  /** Header-right slot — actions (e.g. a delete button). */
  trailingSlot?: ReactNode;
  /** Pointer-down on the whole header row — a header-drag surface (the canvas
   *  note drags by its header). Omit for handle-driven drag (the board). */
  onHeaderPointerDown?: (e: PointerEvent<HTMLDivElement>) => void;
  /** Extra classes for the header row (e.g. `cursor-grab`). */
  headerClassName?: string;
  children?: ReactNode;
}

/**
 * StickyNoteCard — the shared "paper" sticky-note shell: a `⠿ NOTE` header bar
 * over an amber paper body. Data-blind and drag-agnostic: the container is the
 * forwarded ref (so @dnd-kit or absolute positioning attaches to it), the header
 * exposes `leadingSlot` / `trailingSlot` + an optional `onHeaderPointerDown` drag
 * surface, and the note body is `children`. Consumed by the Yjs canvas
 * `StickyNoteLayer` (PB-234) and the Hoshin workshop note cards, so the look
 * lives in exactly one place.
 *
 * The amber surface is a deliberately theme-invariant "paper" colour with no
 * pb-token equivalent (preserving the shipped look), so the token-contract rule
 * is disabled inline on those className lines.
 */
export const StickyNoteCard = forwardRef<HTMLDivElement, StickyNoteCardProps>(
  function StickyNoteCard(
    {
      label = "NOTE",
      leadingSlot,
      trailingSlot,
      onHeaderPointerDown,
      headerClassName,
      className,
      children,
      ...rest
    },
    ref,
  ) {
    return (
      <div
        ref={ref}
        className={cn(
          // eslint-disable-next-line no-restricted-syntax -- theme-invariant amber "paper" sticky note; raw palette intentional (no pb-token equivalent, preserves the shipped PB-234 look)
          "flex flex-col rounded-lg border border-amber-300/80 bg-amber-100 text-amber-950 shadow-md dark:border-amber-400/50 dark:bg-amber-300 dark:text-amber-950",
          className,
        )}
        {...rest}
      >
        <div
          onPointerDown={onHeaderPointerDown}
          className={cn(
            // eslint-disable-next-line no-restricted-syntax -- amber "paper" note header divider (raw palette intentional, see above)
            "flex items-center justify-between gap-2 border-b border-amber-300/70 px-2 py-1",
            headerClassName,
          )}
        >
          <div className="inline-flex min-w-0 items-center gap-1.5 text-dense-1 font-semibold uppercase tracking-wider opacity-70">
            {leadingSlot ?? <GripVertical className="h-3 w-3" aria-hidden />}
            {label}
          </div>
          {trailingSlot != null &&
            (onHeaderPointerDown ? (
              // Keep action-slot pointer-downs (e.g. a delete button) from
              // bubbling to the header's drag surface. `display: contents` adds
              // no box, so the flex layout is unchanged.
              <span
                style={{ display: "contents" }}
                onPointerDown={(e) => e.stopPropagation()}
              >
                {trailingSlot}
              </span>
            ) : (
              trailingSlot
            ))}
        </div>
        {children}
      </div>
    );
  },
);
